import {
  Component,
  Input,
  Output,
  ContentChild,
  ContentChildren,
  ElementRef,
  ViewChild,
  EventEmitter,
  NgZone,
  forwardRef,
  QueryList,
  NgModule,
  ViewEncapsulation,
  Optional,
  Inject
} from '@angular/core';

import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor
} from '@angular/forms';

import { CommonModule } from '@angular/common';
import {
  MdPlaceholder,
  MdHint
} from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { InputConverter } from '../../../decorators';
import { OHTMLInputComponent } from '../../input/html-input/o-html-input.component';
import { OSharedModule } from '../../../shared';

const noop = () => {
  //nothing to do
};

// Control Value accessor provider
const CKEDITOR_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CKEditor),
  multi: true
};

let nextUniqueId = 0;

/**
 * CKEditor component
 * Usage :
 *  <ckeditor [(ngModel)]="data" [config]="{...}" debounce="500"></ckeditor>
 */

export class CKEditor implements ControlValueAccessor {

  /**
   * Bindings.
   */
   config;
   debounce;

   id: string = `ckeditor-${nextUniqueId++}`;
   @InputConverter() floatingPlaceholder: boolean = true;
   hintLabel: string = '';
   @InputConverter() readonly: boolean = false;
   placeholder: string = null;
   dividerColor: 'primary' | 'accent' | 'warn' = 'primary';

  protected disabled = false;
  /**
   * Content directives.
   */
   _placeholderChild: MdPlaceholder;
   _hintChildren: QueryList<MdHint>;

  /** Readonly properties. */
  get focused() { return this._focused; }
  get empty() { return this._value === null || this._value === ''; }
  get characterCount(): number {
    return this.empty ? 0 : ('' + this._value).length;
  }
  get inputId(): string { return `${this.id}-input`; }

  // @Output('change') change = new EventEmitter();
  
  get onChange(): Observable<any> {
    return this._changeEmitter.asObservable();
  }

  
  get onBlur(): Observable<FocusEvent> {
    return this._blurEmitter.asObservable();
  }

  
  get onFocus(): Observable<FocusEvent> {
    return this._focusEmitter.asObservable();
  }

  get isDisabled() : boolean {
    return this.disabled;
  }


   host;
  instance;
  debounceTimeout;
  setInstanceDataTimeout;
  setReadOnlyStateTimeout;
  elementRef;
  zone;

  private _focused: boolean = false;
  private _value: any = '';
  private _isReadOnly: boolean = false;

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _changeEmitter: EventEmitter<any> = new EventEmitter<any>();

  /** Callback registered via registerOnTouched (ControlValueAccessor) */
  private _onTouchedCallback: () => void = noop;

  /** Callback registered via registerOnChange (ControlValueAccessor) */
  private _onChangeCallback: (_: any) => void = noop;


  /**
   * Constructor
   */
  constructor(
    elementRef: ElementRef,
    zone: NgZone,
      protected htmlInputComponent: OHTMLInputComponent
  ) {
    this.elementRef = elementRef;
    this.zone = zone;
  }

  get value(): any {
    return this._value;
  }

  
  set value(v) {
    if (v !== this._value) {
      this._value = v;
      this._onChangeCallback(v);
    }
  }

  /**
   * On component destroy
   */
  ngOnDestroy() {
    this.destroyCKEditor();
  }

  public destroyCKEditor() {
    if (this.instance) {
      // workaround for deleting dom element
      let editorDom = document.getElementsByClassName(this.instance.id)[0];
      this.instance.destroy(true);
      if (editorDom) {
        editorDom.remove();
      }
      this.instance = null;
    }
  }

  /**
   * On component view init
   */
  ngAfterViewInit() {
    this.initializeCkEditor();
  }

  initializeCkEditor(value?) {
    if (this.htmlInputComponent.isLoaded()) {
      // Configuration
      var config = this.config || {};
      this.ckeditorInit(config, value);
    }
  }

  /**
   * Value update process
   */
  updateValue(value) {
    this.zone.run(() => {
      this.value = value;

      // this.onChange(value);

      this._onTouchedCallback();
      this._changeEmitter.emit(value);
    });
  }

  /**
   * CKEditor init
   */
  ckeditorInit(config, value?) {
    if (!window['CKEDITOR']) {
      console.error('Please include CKEditor in your page');
      return;
    }

    // CKEditor replace textarea
    this.instance = window['CKEDITOR'].replace(this.host.nativeElement, config);
    var self = this;
    this.instance.once('instanceReady', function () {
      // Set initial value
      self.setInstanceData(value || '');

      // ensuring readOnly state
      self.setReadOnlyState(self._isReadOnly);

      // CKEditor events
      self.instance.on('change', (evt: any) => self._handleChange(evt));
      self.instance.on('blur', (evt: any) => self._handleBlur(evt));
      self.instance.on('focus', (evt: any) => self._handleFocus(evt));
    });
  }

  /**
   * Implements ControlValueAccessor
   */
  writeValue(value) {
    this._value = value;
    // if (this.instance) {
    // this.instance.setData(value);
    this.setInstanceData(value);
    // }
  }

  /**
 * Implemented as part of ControlValueAccessor.
 */
  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   */
  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  /** Set focus on input */
  focus() {
    this.host.nativeElement.focus();
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this._onTouchedCallback();
    let value = this.instance.getData();

    // Debounce update
    if (this.debounce) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.updateValue(value);
        this.debounceTimeout = null;
      }, parseInt(this.debounce));

      // Live update
    } else {
      this.updateValue(value);
    }
  }

  _hasPlaceholder(): boolean {
    return !!this.placeholder || this._placeholderChild !== null;
  }

  setReadOnlyState(value: boolean) {
    this._isReadOnly = value;
    if (this.instance) {
      var self = this;
      var retryCount = 0;
      var delayedSetReadOnly = function () {
        var status = self.instance.status;
        if (status !== 'loaded' && retryCount++ < 10) {
          self.setReadOnlyStateTimeout = setTimeout(delayedSetReadOnly, retryCount * 10);
          //Wait a while longer each iteration
        } else {
          self.instance.setReadOnly(self._isReadOnly);
          self.setReadOnlyStateTimeout = null;
        }
      };
      if (this.setReadOnlyStateTimeout) {
        clearTimeout(this.setReadOnlyStateTimeout);
      }
      this.setReadOnlyStateTimeout = setTimeout(delayedSetReadOnly, 50);
    }
  }

  protected setInstanceData(data: any) {
    if (this.instance) {
      var self = this;
      var retryCount = 0;
      var delayedSetData = function () {
        var status = self.instance.status;
        if (status !== 'loaded' && retryCount++ < 10) {
          self.setInstanceDataTimeout = setTimeout(delayedSetData, retryCount * 10); //Wait a while longer each iteration
        } else {
          self.instance.setData(self._value);
          self.setInstanceDataTimeout = null;
        }
      };
      if (this.setInstanceDataTimeout) {
        clearTimeout(this.setInstanceDataTimeout);
      }
      this.setInstanceDataTimeout = setTimeout(delayedSetData, 50);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'ckeditor',
  providers: [CKEDITOR_CONTROL_VALUE_ACCESSOR],
  template: `
    <div class="mat-ckeditor-wrapper">
      <div class="mat-ckeditor-table">
        <div class="mat-ckeditor-prefix"><ng-content select="[mat-prefix]"></ng-content></div>

        <div class="mat-ckeditor-infix">
          <textarea #host></textarea>

          <label class="mat-ckeditor-placeholder"
                 [attr.for]="inputId"
                 [class.mat-empty]="empty"
                 [class.mat-focused]="focused"
                 [class.mat-float]="floatingPlaceholder"
                 [class.mat-accent]="dividerColor == 'accent'"
                 [class.mat-warn]="dividerColor == 'warn'"
                 *ngIf="_hasPlaceholder()">
            <ng-content select="mat-placeholder"></ng-content>
            {{placeholder}}
            <span class="mat-placeholder-required" *ngIf="required">*</span>
          </label>
        </div>

        <div class="mat-ckeditor-suffix"><ng-content select="[mat-suffix]"></ng-content></div>
      </div>

      <div class="mat-ckeditor-underline"
           [class.mat-disabled]="isDisabled">
        <span class="mat-ckeditor-ripple"
              [class.mat-focused]="focused"
              [class.mat-accent]="dividerColor == 'accent'"
              [class.mat-warn]="dividerColor == 'warn'"></span>
      </div>

      <div *ngIf="hintLabel != ''" class="mat-hint">{{hintLabel}}</div>
      <ng-content select="md-hint"></ng-content>
    </div>
  `,
  styles: [`
    :-moz-ui-invalid{box-shadow:none}.ckeditor{height:auto}ckeditor{display:inline-block;position:relative;text-align:left}[dir='rtl'] ckeditor{text-align:right}.mat-ckeditor-wrapper{margin:16px 0}.mat-ckeditor-table{display:inline-table;flex-flow:column;vertical-align:bottom;width:100%}.mat-ckeditor-table>*{display:table-cell}.mat-ckeditor-infix{position:relative}.mat-ckeditor-placeholder{position:absolute;left:0;top:0;font-size:100%;pointer-events:none;z-index:1;width:100%;display:none;white-space:nowrap;text-overflow:ellipsis;overflow-x:hidden}.mat-ckeditor-placeholder.mat-empty{display:block;cursor:text;top:65px}.mat-ckeditor-placeholder.mat-float:not(.mat-empty),.mat-ckeditor-placeholder.mat-float.mat-focused{display:block;padding-bottom:5px;transform:translateY(-100%) scale(.75);width:133.33333%;top:0px}[dir='rtl'] .mat-ckeditor-placeholder{transform-origin:bottom right}input:-webkit-autofill+.mat-ckeditor-placeholder{display:block;padding-bottom:5px;transform:translateY(-100%) scale(.75);width:133.33333%}.mat-ckeditor-underline{position:absolute;height:1px;width:100%;margin-top:4px;border-top-width:1px;border-top-style:solid}.mat-ckeditor-underline.mat-disabled{border-top:0;background-image:linear-gradient(to right, rgba(0,0,0,0.26) 0%, rgba(0,0,0,0.26) 33%, transparent 0%);background-position:0;background-size:4px 1px;background-repeat:repeat-x}.mat-ckeditor-underline .mat-ckeditor-ripple{position:absolute;height:2px;z-index:1;top:-1px;width:100%;opacity:0}.mat-ckeditor-underline .mat-ckeditor-ripple.mat-focused{opacity:1;transform:scaleY(1)}.mat-ckeditor{height:auto}.mat-ckeditor .mat-ckeditor-wrapper .mat-hint{bottom:-0.5em}.mat-ckeditor .cke{border-bottom:none !important}.mat-ckeditor .cke_top{padding:0px !important}
  `],
  encapsulation: ViewEncapsulation.None
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: OHTMLInputComponent, decorators: [{ type: Optional }, { type: Inject, args: [forwardRef(() => OHTMLInputComponent), ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'debounce': [{ type: Input },],
'id': [{ type: Input },],
'floatingPlaceholder': [{ type: Input },],
'hintLabel': [{ type: Input },],
'readonly': [{ type: Input },],
'placeholder': [{ type: Input },],
'dividerColor': [{ type: Input },],
'_placeholderChild': [{ type: ContentChild, args: [MdPlaceholder, ] },],
'_hintChildren': [{ type: ContentChildren, args: [MdHint, ] },],
'onChange': [{ type: Output, args: ['change', ] },],
'onBlur': [{ type: Output, args: ['blur', ] },],
'onFocus': [{ type: Output, args: ['focus', ] },],
'host': [{ type: ViewChild, args: ['host', ] },],
'value': [{ type: Input },],
};
}


export class MdCKEditorModule {
static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  declarations: [CKEditor],
  imports: [OSharedModule, CommonModule],
  exports: [CKEditor],
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
